// Sleep (PLR-10). PURE: the conditions and the resulting time skip, so tests and
// the host agree. Sleeping is allowed at night or during a thunderstorm, with no
// hostile mob within 8 blocks and the bed not already occupied; success sets the
// player's spawn, advances the world to the next morning, clears the rain, and
// resets the phantom (insomnia) counter.
import { SLEEP, TICK } from '../config';

export type SleepResult = 'ok' | 'not_night' | 'monsters_nearby' | 'occupied';

export interface SleepContext {
  /** Time of day, 0..24000. */
  timeOfDay: number;
  /** A thunderstorm lets you sleep during the day too. */
  thunderstorm: boolean;
  /** The bed's `occupied` flag. */
  occupied: boolean;
  /** Distance to the nearest hostile mob (Infinity if none). */
  nearestMonster: number;
}

/** Whether it is a valid time to sleep (night window, or a thunderstorm). */
export function canSleepAt(timeOfDay: number, thunderstorm: boolean): boolean {
  return thunderstorm || (timeOfDay >= SLEEP.NIGHT_START && timeOfDay <= SLEEP.NIGHT_END);
}

/** Multiplayer sleep vote (NET C3): a strict majority of the connected players
 *  being asleep skips the night (a lone player counts; 1 of 2 does not). */
export function sleepMajority(sleeping: number, total: number): boolean {
  return total > 0 && sleeping * 2 > total;
}

/** Evaluate a sleep attempt against the current conditions (PLR-10, E-12/E-18). */
export function evaluateSleep(c: SleepContext): SleepResult {
  if (c.occupied) return 'occupied';
  if (!canSleepAt(c.timeOfDay, c.thunderstorm)) return 'not_night';
  if (c.nearestMonster < SLEEP.MONSTER_RADIUS) return 'monsters_nearby';
  return 'ok';
}

/** The absolute `dayTime` a successful sleep advances to — the next morning. */
export function wakeTime(dayTime: number): number {
  const day = Math.floor(dayTime / TICK.DAY_TICKS);
  return (day + 1) * TICK.DAY_TICKS + SLEEP.WAKE_TIME;
}

/** A human message for a failed attempt (UI feedback). */
export function sleepMessage(r: SleepResult): string {
  switch (r) {
    case 'not_night': return 'You can only sleep at night or during thunderstorms';
    case 'monsters_nearby': return "You may not rest now; there are monsters nearby";
    case 'occupied': return 'This bed is occupied';
    default: return '';
  }
}
